'use client';

import { useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import Link from 'next/link';
import { ChevronDown, ChevronsUpDown } from 'lucide-react';
import { House } from '@phosphor-icons/react';
import { useProjects } from '../hooks/use-projects';
import { useNewProjectStore } from '../model/new-project-store';
import { ProjectListMenu } from './ProjectListMenu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/shadcn/popover';
import { cn } from '@/shared/lib/cn';
import { useSidebarStore } from '@/shared/stores/sidebar-store';

interface ProjectSwitcherProps {
  currentProjectId?: string;
  currentProjectName?: string;
  className?: string;
  /**
   * `sidebar` (default) — the left-nav project row.
   * `title` — the hero title itself doubles as the switcher (building name +
   *   up/down chevron, no leading icon) on the project page header. Mirrors the
   *   Apple hero switcher.
   */
  variant?: 'sidebar' | 'title';
}

/**
 * Switches between the user's buildings. The switching affordance (chevron /
 * popover) only appears when there is more than one building — with a single
 * building there is nothing to switch to, so we show a plain label. New
 * buildings are created from the popover or the project header ⋮ menu.
 */
export function ProjectSwitcher({
  currentProjectId,
  currentProjectName,
  className,
  variant = 'sidebar',
}: ProjectSwitcherProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [open, setOpen] = useState(false);
  const collapsed = useSidebarStore((s) => s.collapsed);
  const openNewProject = useNewProjectStore((s) => s.open);
  const { data: projectsResponse } = useProjects();
  const projects = projectsResponse?.data || [];
  const hasMultiple = projects.length > 1;

  const isActive = currentProjectId
    ? pathname === `/project/${currentProjectId}`
    : false;

  const handleProjectSelect = (projectId: string) => {
    setOpen(false);
    router.push(`/project/${projectId}`);
  };

  const handleCreate = () => {
    setOpen(false);
    openNewProject();
  };

  const menu = (
    <ProjectListMenu
      projects={projects}
      currentProjectId={currentProjectId}
      onSelect={handleProjectSelect}
      onCreate={handleCreate}
    />
  );

  // Hero title for the project page header. The large title itself is the
  // switcher: building name + up/down chevron, no leading icon (parity with the
  // Apple hero). A single building shows a plain, non-interactive title.
  if (variant === 'title') {
    // Split layout (margins) from typography. The hero type role lives on the
    // text-bearing element itself, NOT inherited down from the <h1>: a nested
    // <button> would otherwise depend on Preflight's `font: inherit` reset to
    // pick up the 45px size, which is fragile (and silently regressed the hero
    // to the UA default button size). Applying the role directly keeps the
    // switcher title the same size as the plain title.
    const layoutClass = 'mt-8 pb-6';
    const typographyClass =
      'text-screen-hero-title tracking-tight text-foreground';
    if (!hasMultiple) {
      return (
        <h1 className={cn(layoutClass, typographyClass, className)}>
          {currentProjectName || 'Select building'}
        </h1>
      );
    }
    return (
      // <button> nested in <h1> keeps the heading semantic while making the
      // title the menu trigger; the hero type role is applied to the button so
      // it never relies on font inheritance.
      <h1 className={cn(layoutClass, className)}>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              className={cn(
                'flex items-center gap-2 text-left max-w-full',
                typographyClass
              )}
              aria-label={currentProjectName || 'Switch building'}
            >
              <span className="truncate">
                {currentProjectName || 'Select building'}
              </span>
              <ChevronsUpDown className="h-7 w-7 flex-shrink-0 text-muted-foreground" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-1" align="start">
            {menu}
          </PopoverContent>
        </Popover>
      </h1>
    );
  }

  // Collapsed sidebar: an icon-only entry.
  if (collapsed) {
    if (!hasMultiple) {
      return (
        <Link
          href={currentProjectId ? `/project/${currentProjectId}` : '#'}
          className={cn(
            'w-full flex items-center justify-center py-2 rounded-lg text-list-item-subtitle transition-colors',
            isActive
              ? 'bg-foreground/10 text-primary font-medium'
              : 'text-foreground hover:bg-foreground/5',
            className
          )}
          title={currentProjectName || 'Select building'}
        >
          <House
            className="h-5 w-5 text-primary"
            weight={isActive ? 'fill' : 'regular'}
          />
        </Link>
      );
    }
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            className={cn(
              'w-full flex items-center justify-center py-2 rounded-lg text-list-item-subtitle transition-colors',
              isActive
                ? 'bg-foreground/10 text-primary font-medium'
                : 'text-foreground hover:bg-foreground/5',
              className
            )}
            title={currentProjectName || 'Select building'}
          >
            <House
              className="h-5 w-5 text-primary"
              weight={isActive ? 'fill' : 'regular'}
            />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-1" align="start" side="right">
          {menu}
        </PopoverContent>
      </Popover>
    );
  }

  // Expanded sidebar row: project name link + (optional) switcher chevron.
  return (
    <div
      className={cn(
        'w-full flex items-center rounded-lg text-list-item-subtitle transition-colors relative',
        isActive
          ? 'bg-foreground/10 text-primary font-medium'
          : 'text-foreground hover:bg-foreground/5',
        className
      )}
    >
      <Link
        href={currentProjectId ? `/project/${currentProjectId}` : '#'}
        className="flex items-center gap-3 px-3 py-2 truncate flex-1 min-w-0"
      >
        <House
          className="h-5 w-5 flex-shrink-0 text-primary"
          weight={isActive ? 'fill' : 'regular'}
        />
        <span className="truncate">
          {currentProjectName || 'Select building'}
        </span>
      </Link>

      {hasMultiple && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              className="ml-auto min-w-6 flex items-center justify-center p-0.5 rounded-md hover:bg-foreground/10 transition-colors flex-shrink-0 mr-2"
              aria-label="Switch building"
            >
              <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-56 p-1" align="start">
            {menu}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
